import React, { type ReactElement, cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Tooltip } from '@arco-design/web-react';

import { IconFont } from '@/components/IconFont';
import { cs } from '@/utils/className';
import { mergePrefix } from '@/utils/useUrlParams';

import styles from './index.module.less';

export function RenderIcon({ Icon, style = null, size = '' }: { Icon: any; style?: React.CSSProperties; size?: number | string }) {
  /** 与 Avatar 传入的 fontSize / size 对齐，避免 SVG 默认 20、iconfont 24 视觉不一致 */
  const resolvedSize =
    size !== '' && size != null
      ? Number(size)
      : typeof style?.fontSize === 'string'
        ? parseInt(String(style.fontSize), 10) || 24
        : typeof style?.fontSize === 'number'
          ? style.fontSize
          : 24;
  const iconStyle: React.CSSProperties = { marginRight: 12, ...style, fontSize: `${resolvedSize}px` };

  if (!Icon) return null;

  if (typeof Icon === 'string') {
    return <IconFont style={iconStyle} name={Icon} />;
  }

  // SVG 组件：尺寸与 iconfont 一致；颜色只依赖 style.opacity，不再叠加灰色 class（否则会双重变淡）
  return <Icon size={resolvedSize} style={iconStyle} />;
}

interface CustomAvatarProps {
  /** 容器类名 */
  className?: string;
  /** 图片 src */
  avatar?: string;
  /** 兼容 icon: iconfont 和 React 组件(旧) */
  icon?: any;
  /** 图标大小 */
  iconSize?: number | string;
  /** 标题 */
  title: React.ReactNode;
  /** 描述（支持字符串或自定义节点，如到期提示） */
  desc?: React.ReactNode;
  /** 描述气泡纯文本；不传时若 desc 为 string 则用 desc */
  descTooltipText?: string;
  /** 跳转链接 */
  to?: string;
  /** 点击事件 */
  click?: () => void;
  /** 取消链接: 纯展示 */
  noLink?: boolean;
  /** 描述气泡提示 */
  descTooltip?: boolean;
  /** 描述最大展示行数，默认单行 */
  descLines?: 1 | 2;
  /** 图标和文本间距 */
  gap?: number;
}

interface DescriptionTooltipProps {
  /** 完整描述内容 */
  description: string;
  /** 触发悬浮提示的描述节点 */
  children: ReactElement;
  /**
   * 为 true 时仅在文案被截断（scrollHeight > clientHeight）时启用 Tooltip；
   * 完整展示则不挂 hover 提示。
   */
  onlyWhenOverflow?: boolean;
}

/** 仓库描述悬浮提示：统一长文本宽高、滚动和顶栏避让行为 */
export function DescriptionTooltip({ description, children, onlyWhenOverflow = false }: DescriptionTooltipProps) {
  const triggerRef = useRef<HTMLElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);

  const measureOverflow = useCallback(() => {
    const el = triggerRef.current;
    if (!el) {
      setOverflowing(false);
      return;
    }
    // line-clamp / 单行省略：内容高度超出可视区即视为截断
    setOverflowing(el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    if (!onlyWhenOverflow) return;
    measureOverflow();
    const el = triggerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measureOverflow());
    observer.observe(el);
    return () => observer.disconnect();
  }, [description, measureOverflow, onlyWhenOverflow]);

  const setTriggerRef = useCallback(
    (node: HTMLElement | null) => {
      triggerRef.current = node;
      const childRef = (children as ReactElement & { ref?: React.Ref<HTMLElement> }).ref;
      if (typeof childRef === 'function') childRef(node);
      else if (childRef && typeof childRef === 'object') (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
      if (onlyWhenOverflow && node) {
        // 挂载后再量一次，避免首帧尚未应用 line-clamp
        requestAnimationFrame(measureOverflow);
      }
    },
    [children, measureOverflow, onlyWhenOverflow],
  );

  const trigger = onlyWhenOverflow ? cloneElement(children, { ref: setTriggerRef }) : children;
  const showTooltip = Boolean(description && description !== '-' && (!onlyWhenOverflow || overflowing));

  // 文案未截断时不包 Tooltip，避免无意义 hover
  if (onlyWhenOverflow && !showTooltip) {
    return trigger;
  }

  return (
    <Tooltip
      content={showTooltip ? <div className={styles.tooltipDescription}>{description}</div> : undefined}
      style={{ zIndex: 1100 }}
      triggerProps={{ boundaryDistance: { top: 72, left: 8 } }}
      mini
    >
      {trigger}
    </Tooltip>
  );
}

function Avatar({
  className,
  avatar,
  icon: Icon,
  iconSize = 24,
  title,
  desc,
  descTooltipText,
  to,
  click,
  noLink,
  descTooltip,
  descLines = 1,
  gap = 10,
}: CustomAvatarProps) {
  const renderTitle = () => {
    if (to) {
      return (
        <Link style={{ color: 'rgb(var(--link-6))' }} to={mergePrefix(to)}>
          {title}
        </Link>
      );
    }
    if (click) {
      return (
        <span className={styles.click} onClick={click}>
          {title}
        </span>
      );
    }
    return title;
  };

  const tooltipDescription = descTooltipText ?? (typeof desc === 'string' ? desc : '');

  return (
    <div
      className={cs(styles.wrapper, className, {
        [styles.link]: !noLink || to,
      })}
    >
      {avatar ? (
        <img className={styles.image} src={avatar} alt="" />
      ) : (
        <RenderIcon Icon={Icon} size={iconSize} style={{ marginRight: gap, opacity: 0.45 }} />
      )}
      <div className={styles.text}>
        <div className={styles.title}>{renderTitle()}</div>
        {desc && (
          <DescriptionTooltip description={tooltipDescription}>
            <div className={cs(styles.description, 'ks-avatar-desc', { [styles.descriptionMultiline]: descLines === 2 })}>{desc}</div>
          </DescriptionTooltip>
        )}
      </div>
    </div>
  );
}

export default Avatar;
